const DESKTOP_FLAG = "prime-desktop-shell";
const HANDOFF_FLAG = "prime-desktop-handoff";

type TauriWindow = Window & {
  __TAURI_INTERNALS__?: unknown;
  __TAURI__?: {
    opener?: {
      openUrl?: (url: string) => Promise<void>;
    };
  };
};

function tauriWindow(): TauriWindow | null {
  if (typeof window === "undefined") {
    return null;
  }
  return window as TauriWindow;
}

/**
 * True only inside the Mortgage Broker Assistant native WebView.
 */
export function isTauriRuntime(): boolean {
  const w = tauriWindow();
  if (!w) {
    return false;
  }
  return Boolean(w.__TAURI_INTERNALS__ || w.__TAURI__);
}

/**
 * True when the web app is running inside the Prime Copilot Tauri shell.
 */
export function isDesktopShell(): boolean {
  if (isTauriRuntime()) {
    try {
      sessionStorage.setItem(DESKTOP_FLAG, "1");
    } catch {
      /* private mode */
    }
    return true;
  }
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("client") === "desktop" && params.get("handoff") !== "desktop") {
      sessionStorage.setItem(DESKTOP_FLAG, "1");
      return true;
    }
    return sessionStorage.getItem(DESKTOP_FLAG) === "1";
  } catch {
    return false;
  }
}

/**
 * Safari/Chrome login started from the desktop app so passkeys and Google work.
 */
export function isBrowserAuthHandoff(): boolean {
  if (isTauriRuntime()) {
    return false;
  }
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("handoff") === "desktop") {
      sessionStorage.setItem(HANDOFF_FLAG, "1");
      return true;
    }
    return sessionStorage.getItem(HANDOFF_FLAG) === "1";
  } catch {
    return false;
  }
}

export function googleSignInUrl(apiBase: string, redirectTo: string, desktop: boolean): string {
  const url = new URL(`${apiBase.replace(/\/$/, "")}/auth/google`);
  url.searchParams.set("redirectTo", redirectTo);
  if (desktop) {
    url.searchParams.set("desktop", "1");
  }
  return url.toString();
}

export function browserLoginHandoffUrl(
  origin: string,
  input: { email?: string; next?: string },
): string {
  const url = new URL("/login", origin);
  url.searchParams.set("handoff", "desktop");
  if (input.next) {
    url.searchParams.set("next", input.next);
  }
  if (input.email) {
    url.searchParams.set("email", input.email);
  }
  return url.toString();
}

export function browserSessionUrl(origin: string, token: string, next: string): string {
  const url = new URL("/auth/callback", origin);
  url.searchParams.set("token", token);
  url.searchParams.set("next", next);
  return url.toString();
}

export function desktopReturnHandoffUrl(
  origin: string,
  token: string,
  next: string,
): string {
  const url = new URL("/auth/desktop-return", origin);
  url.searchParams.set("token", token);
  url.searchParams.set("next", next);
  return url.toString();
}

export function desktopAppAuthUrl(token: string, next: string): string {
  const url = new URL("primeassistant://auth/callback");
  url.searchParams.set("token", token);
  url.searchParams.set("next", next);
  return url.toString();
}

export async function openInSystemBrowser(url: string): Promise<boolean> {
  const opener = tauriWindow()?.__TAURI__?.opener?.openUrl;
  if (!opener) {
    return false;
  }
  await opener(url);
  return true;
}
